import React, { useState } from 'react';
import { Plus, X, Settings, Trash2, Edit3, Save, Film, Upload, GripVertical, Eye, EyeOff, Lock, Unlock, Archive } from 'lucide-react';
import { 
  createSurveyCategory, 
  updateSurveyCategory, 
  deleteSurveyCategory, 
  updateCategoryOrder,
  updateCategoryVisibility,
  closeSurveyCategory,
  reopenSurveyCategory,
  archiveSurveyCategory
} from '../../services/surveyService';

const SurveyCategoryManager = ({ 
  isVisible, 
  onClose, 
  allMovies, 
  onCategoryCreated,
  existingCategories = [],
  currentUser
}) => {
  const [mode, setMode] = useState('create'); // 'create', 'edit', 'list'
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    movieIds: [],
    headerImageUrl: ''
  });
  const [selectedMovies, setSelectedMovies] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  const resetForm = () => {
    setFormData({ name: '', description: '', movieIds: [], headerImageUrl: '' });
    setSelectedMovies(new Set());
    setSearchTerm('');
    setError('');
    setSuccess('');
    setEditingCategory(null);
  };

  const handleClose = () => {
    resetForm();
    setMode('create');
    onClose();
  };

  const handleMovieToggle = (movieId) => {
    const newSelected = new Set(selectedMovies);
    if (newSelected.has(movieId)) {
      newSelected.delete(movieId);
    } else {
      newSelected.add(movieId);
    }
    setSelectedMovies(newSelected);
    setFormData(prev => ({
      ...prev,
      movieIds: Array.from(newSelected)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Category name is required');
      return;
    }
    
    if (formData.movieIds.length === 0) {
      setError('Please select at least one movie');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (mode === 'create') {
        const { error: createError } = await createSurveyCategory({
          name: formData.name.trim(),
          description: formData.description.trim(),
          movieIds: formData.movieIds,
          headerImageUrl: formData.headerImageUrl
        }, currentUser);
        
        if (createError) throw createError;
        setSuccess(`Category "${formData.name}" created successfully!`);
      } else if (mode === 'edit' && editingCategory) {
        const { error: updateError } = await updateSurveyCategory(editingCategory.id, {
          name: formData.name.trim(),
          description: formData.description.trim(),
          movieIds: formData.movieIds, // Use consistent naming - service will convert to movie_ids
          headerImageUrl: formData.headerImageUrl
        });
        
        if (updateError) throw updateError;
        setSuccess(`Category "${formData.name}" updated successfully!`);
      }
      
      onCategoryCreated(); // Refresh categories in parent
      
      setTimeout(() => {
        resetForm();
        setMode('list');
      }, 2000);
      
    } catch (err) {
      console.error('Error saving category:', err);
      setError(err.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      movieIds: category.movie_ids || [],
      headerImageUrl: category.header_image_url || ''
    });
    setSelectedMovies(new Set(category.movie_ids || []));
    setMode('edit');
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Are you sure you want to delete the category "${category.name}"? This will also delete all votes for this category.`)) {
      return;
    }

    console.log('Attempting to delete category:', category.id);
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const { error } = await deleteSurveyCategory(category.id, currentUser);
      console.log('Delete result:', { error });
      
      if (error) {
        console.error('Delete error details:', error);
        throw error;
      }
      
      setSuccess(`Category "${category.name}" deleted successfully!`);
      console.log('Calling onCategoryCreated to refresh category list');
      
      // Add a small delay to ensure database has processed the delete
      setTimeout(() => {
        onCategoryCreated(); // Refresh categories
      }, 100);
      
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(`Failed to delete category: ${err.message}`);
      
      setTimeout(() => {
        setError('');
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  const filteredMovies = allMovies.filter(movie =>
    movie.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (movie.year && movie.year.toString().includes(searchTerm))
  );

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    // Reorder categories
    const reorderedCategories = [...existingCategories];
    const draggedCategory = reorderedCategories[draggedIndex];
    
    // Remove from old position
    reorderedCategories.splice(draggedIndex, 1);
    // Insert at new position
    reorderedCategories.splice(dropIndex, 0, draggedCategory);

    // Update display_order for all categories
    const categoryUpdates = reorderedCategories.map((category, index) => ({
      id: category.id,
      display_order: index
    }));

    try {
      setLoading(true);
      const { error } = await updateCategoryOrder(categoryUpdates);
      
      if (error) {
        throw error;
      }
      
      setSuccess('Category order updated successfully!');
      onCategoryCreated(); // Refresh the categories
      
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Error updating category order:', err);
      setError(err.message || 'Failed to update category order');
      
      setTimeout(() => {
        setError('');
      }, 3000);
    } finally {
      setLoading(false);
      setDraggedIndex(null);
    }
  };

  // Image upload handler
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image file must be less than 5MB');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setImageUploading(true);
    
    try {
      // Convert to base64 data URL for storage
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({
          ...prev,
          headerImageUrl: event.target.result
        }));
        setImageUploading(false);
      };
      reader.onerror = () => {
        setError('Failed to read image file');
        setImageUploading(false);
        setTimeout(() => setError(''), 3000);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image');
      setImageUploading(false);
      setTimeout(() => setError(''), 3000);
    }
  };

  // Survey Management Handlers
  const handleToggleVisibility = async (category) => {
    setLoading(true);
    setError('');
    
    try {
      const { error } = await updateCategoryVisibility(category.id, !category.is_public);
      if (error) throw error;
      
      setSuccess(`Category "${category.name}" ${category.is_public ? 'hidden from' : 'made visible to'} non-admins`);
      onCategoryCreated(); // Refresh categories
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(`Failed to update visibility: ${err.message}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSurvey = async (category) => {
    if (!window.confirm(`Close survey "${category.name}"? This will lock voting but keep results visible.`)) return;
    
    setLoading(true);
    setError('');
    
    try {
      const { error } = await closeSurveyCategory(category.id);
      if (error) throw error;
      
      setSuccess(`Survey "${category.name}" closed successfully`);
      onCategoryCreated(); // Refresh categories
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(`Failed to close survey: ${err.message}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleReopenSurvey = async (category) => {
    setLoading(true);
    setError('');
    
    try {
      const { error } = await reopenSurveyCategory(category.id);
      if (error) throw error;
      
      setSuccess(`Survey "${category.name}" reopened for voting`);
      onCategoryCreated(); // Refresh categories
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(`Failed to reopen survey: ${err.message}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveSurvey = async (category) => {
    if (!window.confirm(`Archive survey "${category.name}"? This will move it to past results and remove it from the manage view.`)) return;
    
    setLoading(true);
    setError('');
    
    try {
      const { error } = await archiveSurveyCategory(category.id);
      if (error) throw error;
      
      setSuccess(`Survey "${category.name}" archived successfully`);
      onCategoryCreated(); // Refresh categories
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(`Failed to archive survey: ${err.message}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-900 to-orange-900 px-6 py-4 border-b-2 border-orange-500">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-orange-100 flex items-center gap-2">
              <Settings size={20} />
              Survey Category Manager
            </h2>
            <button
              onClick={handleClose}
              className="text-orange-300 hover:text-orange-100 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
          <p className="text-orange-200 text-sm mt-1">
            Create and manage survey categories for users to vote on
          </p>
        </div>

        <div className="flex h-[calc(90vh-120px)]">
          {/* Sidebar */}
          <div className="w-64 bg-gray-700 border-r border-orange-500 p-4 flex flex-col">
            <div className="space-y-2 flex-shrink-0">
              <button
                onClick={() => { resetForm(); setMode('create'); }}
                className={`w-full text-left px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                  mode === 'create' ? 'bg-orange-600 text-white' : 'text-orange-300 hover:bg-gray-600'
                }`}
              >
                <Plus size={16} />
                Create New
              </button>
              <button
                onClick={() => setMode('list')}
                className={`w-full text-left px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                  mode === 'list' ? 'bg-orange-600 text-white' : 'text-orange-300 hover:bg-gray-600'
                }`}
              >
                <Settings size={16} />
                Manage Existing ({existingCategories.length})
              </button>
            </div>

            {mode === 'list' && (
              <div className="mt-4 flex flex-col flex-1 min-h-0">
                <div className="flex items-center justify-between flex-shrink-0 mb-2">
                  <h4 className="text-orange-300 font-semibold text-sm">Categories:</h4>
                  <p className="text-gray-400 text-xs">Drag to reorder</p>
                </div>
                <div className="flex-1 overflow-y-auto space-y-2 pr-2 min-h-0">
                {existingCategories.map((category, index) => (
                  <div 
                    key={category.id} 
                    className={`bg-gray-600 rounded-lg p-3 cursor-move transition-all ${
                      draggedIndex === index ? 'opacity-50 scale-95' : 'hover:bg-gray-550'
                    }`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Drag Handle */}
                      <div className="text-gray-400 mt-1 cursor-grab active:cursor-grabbing">
                        <GripVertical size={14} />
                      </div>
                      
                      {/* Category Content */}
                      <div className="flex-1">
                        {/* Header Image Preview */}
                        {category.header_image_url && (
                          <div className="mb-2">
                            <img
                              src={category.header_image_url}
                              alt={`${category.name} header`}
                              className="w-full h-auto object-contain max-h-20 rounded border border-orange-500"
                              style={{ minHeight: '40px' }}
                            />
                          </div>
                        )}
                        
                        <div className="font-semibold text-orange-200 text-sm">{category.name}</div>
                        {category.description && (
                          <div className="text-gray-300 text-xs mt-1">{category.description}</div>
                        )}
                        <div className="text-gray-400 text-xs mt-1">
                          {category.movie_ids?.length || 0} movies
                        </div>
                      </div>
                      
                      {/* Status Indicators */}
                      <div className="flex flex-col gap-1 mb-2">
                        {!category.is_public && (
                          <span className="text-xs text-yellow-400 bg-yellow-900 px-2 py-1 rounded-full">
                            Hidden
                          </span>
                        )}
                        {category.is_closed && (
                          <span className="text-xs text-red-400 bg-red-900 px-2 py-1 rounded-full">
                            Closed
                          </span>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-1 mt-1">
                        <button
                          onClick={() => handleToggleVisibility(category)}
                          className={`${category.is_public ? 'text-yellow-400 hover:text-yellow-300' : 'text-green-400 hover:text-green-300'} text-xs p-1 rounded hover:bg-gray-500`}
                          title={category.is_public ? 'Hide from non-admins' : 'Make visible to non-admins'}
                        >
                          {category.is_public ? <EyeOff size={12} /> : <Eye size={12} />}
                        </button>
                        
                        {!category.is_closed ? (
                          <button
                            onClick={() => handleCloseSurvey(category)}
                            className="text-orange-400 hover:text-orange-300 text-xs p-1 rounded hover:bg-gray-500"
                            title="Close survey (lock voting)"
                          >
                            <Lock size={12} />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleReopenSurvey(category)}
                            className="text-green-400 hover:text-green-300 text-xs p-1 rounded hover:bg-gray-500"
                            title="Reopen survey for voting"
                          >
                            <Unlock size={12} />
                          </button>
                        )}
                        
                        {category.is_closed && (
                          <button
                            onClick={() => handleArchiveSurvey(category)}
                            className="text-purple-400 hover:text-purple-300 text-xs p-1 rounded hover:bg-gray-500"
                            title="Archive survey (move to past results)"
                          >
                            <Archive size={12} />
                          </button>
                        )}
                        
                        <button
                          onClick={() => handleEdit(category)}
                          className="text-blue-400 hover:text-blue-300 text-xs p-1 rounded hover:bg-gray-500"
                          title="Edit category"
                        >
                          <Edit3 size={12} />
                        </button>
                        <button
                          onClick={() => handleDelete(category)}
                          className="text-red-400 hover:text-red-300 text-xs p-1 rounded hover:bg-gray-500"
                          title="Delete category"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            {mode === 'list' && !editingCategory ? (
              <div className="text-center py-12">
                <Settings className="w-16 h-16 mx-auto text-gray-500 mb-4" />
                <h3 className="text-xl font-semibold text-gray-400 mb-2">Manage Categories</h3>
                <p className="text-gray-500">
                  Select a category from the sidebar to edit or delete it, or create a new one.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Category Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-orange-300">
                    {mode === 'create' ? 'Create New Category' : 'Edit Category'}
                  </h3>
                  
                  <div>
                    <label className="block text-orange-300 text-sm font-bold mb-2">
                      Category Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Horror Classics, New Releases, etc."
                      className="w-full px-4 py-2 bg-gray-700 border border-orange-500 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-400"
                    />
                  </div>

                  <div>
                    <label className="block text-orange-300 text-sm font-bold mb-2">
                      Description (Optional)
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of this category..."
                      rows={3}
                      className="w-full px-4 py-2 bg-gray-700 border border-orange-500 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-400"
                    />
                  </div>

                  {/* Header Image Upload */}
                  <div>
                    <label className="block text-orange-300 text-sm font-bold mb-2">
                      Header Image (Optional)
                    </label>
                    <div className="space-y-3">
                      {/* Current Image Preview */}
                      {formData.headerImageUrl && (
                        <div className="relative">
                          <img
                            src={formData.headerImageUrl}
                            alt="Category header"
                            className="w-full h-auto object-contain max-h-48 rounded-lg border-2 border-orange-500"
                            style={{ minHeight: '80px' }}
                          />
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, headerImageUrl: '' }))}
                            className="absolute top-2 right-2 bg-red-600 hover:bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm shadow-lg"
                          >
                            ×
                          </button>
                        </div>
                      )}
                      
                      {/* Upload Button */}
                      <div className="flex items-center gap-3">
                        <label className="cursor-pointer bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2">
                          <Upload size={16} />
                          {imageUploading ? 'Uploading...' : 'Choose Image'}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            disabled={imageUploading}
                            className="hidden"
                          />
                        </label>
                        {formData.headerImageUrl && (
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, headerImageUrl: '' }))}
                            className="text-red-400 hover:text-red-300 text-sm"
                          >
                            Remove Image
                          </button>
                        )}
                      </div>
                      <p className="text-gray-400 text-xs">
                        Image will display at its natural dimensions (max 5MB). Wide banners work best for headers.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Movie Selection */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-orange-300">
                      Select Movies ({selectedMovies.size} selected)
                    </h3>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search movies..."
                      className="px-3 py-1 bg-gray-700 border border-orange-500 rounded text-white placeholder-gray-400 text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto border border-gray-600 rounded-lg p-4">
                    {filteredMovies.map(movie => (
                      <div
                        key={movie.id}
                        onClick={() => handleMovieToggle(movie.id)}
                        className={`p-3 rounded-lg cursor-pointer transition-colors border-2 ${
                          selectedMovies.has(movie.id)
                            ? 'border-orange-500 bg-orange-900 bg-opacity-30'
                            : 'border-gray-600 bg-gray-700 hover:border-orange-400'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                            selectedMovies.has(movie.id)
                              ? 'border-orange-500 bg-orange-500'
                              : 'border-gray-400'
                          }`}>
                            {selectedMovies.has(movie.id) && (
                              <div className="w-2 h-2 bg-white rounded"></div>
                            )}
                          </div>
                          <Film size={14} className="text-orange-400" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-orange-200 text-sm truncate">
                              {movie.title}
                            </div>
                            <div className="text-gray-400 text-xs">
                              {movie.year} • {movie.runtime}min
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Error/Success Messages */}
                {error && (
                  <div className="p-3 bg-red-900 border border-red-600 rounded-lg text-red-200 text-sm">
                    {error}
                  </div>
                )}

                {success && (
                  <div className="p-3 bg-green-900 border border-green-600 rounded-lg text-green-200 text-sm">
                    {success}
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-600">
                  <button
                    type="button"
                    onClick={() => { resetForm(); setMode('list'); }}
                    className="bg-gray-600 hover:bg-gray-500 text-white px-6 py-2 rounded-lg font-bold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-orange-600 hover:bg-orange-500 disabled:bg-gray-600 text-white px-6 py-2 rounded-lg font-bold transition-colors flex items-center gap-2"
                  >
                    <Save size={16} />
                    {loading ? 'Saving...' : (mode === 'create' ? 'Create Category' : 'Update Category')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyCategoryManager;
